// src/context/ToastContext.jsx
//
// Lightweight toast system — no external library.
// Supports success, error, warning, info variants.
// Auto-dismisses after 4s (errors after 6s).
// Usage:
//   const { toast } = useToast();
//   toast.success('Note saved');
//   toast.error('Could not save — please try again');

import React, {
  createContext, useContext, useState, useCallback, useRef,
} from 'react';

const ToastContext = createContext(null);

let _id = 0;

const VARIANT = {
  success: { bg: 'var(--green-bg)', border: 'var(--green)', color: 'var(--green)', icon: '✓' },
  error:   { bg: 'var(--red-bg)', border: 'var(--red)', color: 'var(--red)', icon: '✕' },
  warning: { bg: 'var(--amber-bg)', border: 'var(--amber)', color: 'var(--amber-text)', icon: '⚠' },
  info:    { bg: 'var(--blue-bg)', border: 'var(--blue)', color: 'var(--blue)', icon: 'ℹ' },
};

function ToastItem({ toast, onDismiss }) {
  const v = VARIANT[toast.variant] || VARIANT.info;
  return (
    <div
      role="alert"
      aria-live={toast.variant === 'error' ? 'assertive' : 'polite'}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 10,
        background: v.bg, border: `1px solid ${v.border}`,
        borderRadius: 10, padding: '12px 14px',
        boxShadow: '0 4px 16px rgba(13,30,53,0.10)',
        // No minWidth: the stack constrains width on mobile, and a 260px floor
        // would push the toast past the edge of a narrow screen.
        boxSizing: 'border-box',
        animation: 'toast-in 0.2s ease',
        fontFamily: 'var(--font-body)',
      }}
    >
      {/* Icon */}
      <span style={{
        width: 20, height: 20, borderRadius: '50%',
        background: v.border, color: v.color,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 11, fontWeight: 700, flexShrink: 0, marginTop: 1,
      }}>
        {v.icon}
      </span>

      {/* Message + optional action button (e.g. "Refresh") */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ fontSize: 13, color: v.color, lineHeight: 1.5 }}>
          {toast.message}
        </div>
        {toast.action && (
          <button
            onClick={toast.action.onClick}
            style={{
              alignSelf: 'flex-start',
              background: v.color, color: 'var(--text-on-brand-dark)', border: 'none',
              borderRadius: 6, padding: '5px 12px', fontSize: 12, fontWeight: 600,
              cursor: 'pointer', fontFamily: 'var(--font-body)',
            }}
          >
            {toast.action.label}
          </button>
        )}
      </div>

      {/* Dismiss */}
      <button
        onClick={() => onDismiss(toast.id)}
        aria-label="Dismiss notification"
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: v.color, opacity: 0.6, fontSize: 16, lineHeight: 1,
          padding: '0 0 0 4px', flexShrink: 0,
        }}
      >
        ×
      </button>
    </div>
  );
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timers = useRef({});

  const dismiss = useCallback((id) => {
    clearTimeout(timers.current[id]);
    delete timers.current[id];
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const add = useCallback((message, variant = 'info', options = {}) => {
    const id = ++_id;
    // Same message already on screen (e.g. the version check firing on every
    // tab focus) — don't stack a duplicate.
    setToasts(prev => (
      prev.some(t => t.message === message && t.variant === variant)
        ? prev
        : [...prev, { id, message, variant, action: options.action }]
    ));
    // 16 Jul 2026: persistent toasts (options.persistent) never auto-dismiss —
    // needed for "update available," which the person might not notice within
    // the normal 4-6s window and shouldn't lose the chance to act on. Stays
    // until they either click the action or dismiss it manually.
    if (options.persistent) return;
    const duration = variant === 'error' ? 6000 : 4000;
    timers.current[id] = setTimeout(() => dismiss(id), duration);
  }, [dismiss]);

  const toast = {
    success: (msg) => add(msg, 'success'),
    error:   (msg) => add(msg, 'error'),
    warning: (msg) => add(msg, 'warning'),
    info:    (msg) => add(msg, 'info'),
  };

  // Listen for session-expiry events fired by the Axios 401 interceptor.
  // The interceptor can't use React hooks so it dispatches a CustomEvent instead.
  React.useEffect(() => {
    const handler = () => add('Your session expired — please sign in again', 'warning');
    window.addEventListener('previu:session-expired', handler);
    return () => window.removeEventListener('previu:session-expired', handler);
  }, [add]);

  // 16 Jul 2026: new. src/utils/versionCheck.js polls for a newer deployed
  // build (interval + on tab focus) and dispatches this event when this
  // tab is running stale JS — same CustomEvent convention as session-expired
  // above, since that module has no React/toast knowledge of its own.
  // Persistent + an explicit Refresh action, since a background reload
  // would be worse than the staleness itself if it happened mid-task.
  React.useEffect(() => {
    const handler = () => add(
      'A new version of Previu is available.',
      'info',
      { persistent: true, action: { label: 'Refresh now', onClick: () => window.location.reload() } },
    );
    window.addEventListener('previu:update-available', handler);
    return () => window.removeEventListener('previu:update-available', handler);
  }, [add]);

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}

      {/* Toast stack — bottom-right on desktop, top-anchored full-width on
          mobile. Positioning lives in .toast-stack (index.css) because it has
          to change at a breakpoint, which inline styles can't express: at
          390px a bottom-right toast covered the login form's Sign In button
          and its 380px max-width overflowed the screen. */}
      {toasts.length > 0 && (
        <div className="toast-stack" aria-label="Notifications">
          {toasts.map(t => (
            <ToastItem key={t.id} toast={t} onDismiss={dismiss} />
          ))}
        </div>
      )}

      <style>{`
        @keyframes toast-in {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </ToastContext.Provider>
  );
}

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside ToastProvider');
  return ctx;
};
